import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { ArticleHero } from "../ArticleHero";
import maternityHeroImage from "../../assets/images/maternity-outdoor-fall-red-gown investment.webp";
import maternityBridgeImage from "../../assets/images/maternity-bow-bridge-new-york.webp";
import maternityStudioImage from "../../assets/images/maternity-black-cutout-dress-studio-portrait.webp";
import newbornRelatedImage from "../../assets/images/newborn-pink-studio-portrait.webp";
import newbornAgeImage from "../../assets/images/newborn-awake-pink-wrap-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";

const timelineStages = [
  {
    weeks: "20–27",
    title: "Early window",
    text: "A smaller bump and often more energy; useful when schedules or travel make a later date difficult.",
  },
  {
    weeks: "28–34",
    title: "Ideal window",
    text: "Your bump is usually beautifully defined while there is still time to move, pose and enjoy the session comfortably.",
    featured: true,
  },
  {
    weeks: "35–37",
    title: "Later window",
    text: "Still possible when you feel well, with a gentler pace, simple locations and fewer transitions.",
  },
  {
    weeks: "38+",
    title: "Close to arrival",
    text: "Best treated as a flexible option because baby may arrive and comfort can change quickly.",
  },
];

export const BestTimeForMaternityPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page maternity-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Maternity", to: "/resources/maternity/" },
          { label: "When Is the Best Time to Take Maternity Photos?" },
        ]}
        category="Maternity guide"
        description="A practical guide to choosing a session date when your bump is beautifully defined and you still feel comfortable in front of the camera."
        imageAlt="Expectant mother photographed during a soft, elegant maternity session"
        imagePosition="center 43%"
        imageSrc={maternityHeroImage}
        readingTime="7 min read"
        title="When Is the Best Time to Take Maternity Photos?"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout newborn-article-callout--answer">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              For most pregnancies, 28–34 weeks is the best time for maternity photos. Your bump is clearly visible, but
              you are usually still comfortable enough to move and enjoy the session. Twins, health considerations and
              your own energy may make an earlier date a better choice.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#timeline">The maternity timeline</a>
            </li>
            <li>
              <a href="#why-window">Why 28–34 weeks works</a>
            </li>
            <li>
              <a href="#twins">Timing for twins</a>
            </li>
            <li>
              <a href="#earlier-later">Earlier or later sessions</a>
            </li>
            <li>
              <a href="#personal-date">Choose your date</a>
            </li>
            <li>
              <a href="#booking">When to book</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section maternity-timeline-section" id="timeline">
          <p className="newborn-article-kicker">YOUR PREGNANCY, AT A GLANCE</p>
          <h2>Maternity Timeline</h2>
          <p>
            There is no single perfect week for everyone. Use this timeline as a starting point, then choose the window
            that fits how you feel and the kind of photographs you want.
          </p>
          <div aria-label="Maternity photography timing from 20 weeks onward" className="maternity-timeline">
            {timelineStages.map((stage) => (
              <article
                className={
                  stage.featured
                    ? "maternity-timeline__stage maternity-timeline__stage--featured"
                    : "maternity-timeline__stage"
                }
                key={stage.weeks}
              >
                <div className="maternity-timeline__marker">
                  <span>{stage.weeks}</span>
                  <small>weeks</small>
                </div>
                <div className="maternity-timeline__copy">
                  {stage.featured && <strong>RECOMMENDED</strong>}
                  <h3>{stage.title}</h3>
                  <p>{stage.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="newborn-article-section newborn-article-split" id="why-window">
          <div>
            <p className="newborn-article-kicker">THE SWEET SPOT</p>
            <h2>Why 28–34 weeks often works best</h2>
            <p>
              By the beginning of the third trimester, the curve of your bump usually photographs clearly from many
              angles. At the same time, there is often enough energy and mobility for walking, changing positions and
              wearing more than one look.
            </p>
            <p>
              Scheduling before the final weeks also leaves a little breathing room if weather, illness or an unexpected
              appointment means we need to move the session.
            </p>
          </div>
          <ArticlePhoto
            alt="Expectant mother in a black cutout dress during a studio maternity session"
            className="newborn-article-split__photo"
            label="PHOTO: 28–34 WEEKS"
            orientation="portrait"
            src={maternityStudioImage}
          />
        </section>

        <section className="newborn-article-section" id="twins">
          <p className="newborn-article-kicker">EXPECTING MULTIPLES</p>
          <h2>Plan a little earlier for twins</h2>
          <p>
            For twins or higher-order multiples, 24–30 weeks is often a more comfortable target. Bumps tend to show
            earlier, and scheduling sooner can reduce the chance that bed rest, an early delivery or increased fatigue
            changes the plan.
          </p>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>Comfort comes first</h3>
              <p>
                Your medical team knows your pregnancy best. If they recommend an earlier window or activity limits, we
                build the session around that guidance.
              </p>
            </div>
          </aside>
        </section>

        <section
          className="newborn-article-section newborn-article-split newborn-article-split--reverse"
          id="earlier-later"
        >
          <ArticlePhoto
            alt="Relaxed outdoor maternity portrait"
            className="newborn-article-split__photo"
            label="PHOTO: YOUR OWN TIMING"
            src={maternityBridgeImage}
          />
          <div>
            <p className="newborn-article-kicker">A FLEXIBLE WINDOW</p>
            <h2>Can you take maternity photos earlier or later?</h2>
            <p>
              Yes. An earlier session can be ideal for travel, a meaningful season or simply because you feel your best
              then. A session after 35 weeks can also be beautiful if you are comfortable and your pregnancy is
              progressing well.
            </p>
            <p>
              Later sessions work best with an easy-to-reach location, shorter walking distances and a flexible backup
              plan. The goal is never to push through discomfort for a photograph.
            </p>
          </div>
        </section>

        <section className="newborn-article-section" id="personal-date">
          <p className="newborn-article-kicker">MAKE IT PERSONAL</p>
          <h2>How to choose the right date for you</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Start with your due date and count back to the 28–34 week window.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Consider how your energy, mobility and pregnancy symptoms have been changing.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Allow extra time for edited photographs if you want them for a shower, announcement or album.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>
                Choose a weekday or backup date when outdoor weather and family schedules require flexibility.
              </span>
            </li>
          </ul>
        </section>

        <section className="newborn-article-section" id="booking">
          <p className="newborn-article-kicker">RESERVE YOUR SESSION</p>
          <h2>When should you book?</h2>
          <p>
            Reach out during your second trimester when possible—ideally around 18–24 weeks. Booking early gives you
            more choice of dates, especially for weekends and the milder outdoor seasons in Charlotte. We can reserve
            the session first and finalize styling and location closer to the day.
          </p>
        </section>

        <section className="newborn-article-cta maternity-article-cta">
          <div>
            <p className="newborn-article-kicker">YOUR SEASON, BEAUTIFULLY REMEMBERED</p>
            <h2>Not sure which week is right for you?</h2>
            <p>
              Compare the maternity session details and pricing. If you need help choosing a comfortable week, send me
              your due date and ideas.
            </p>
          </div>
          <GuideCtaActions category="maternity" />
        </section>

        <section className="newborn-related-guides">
          <header>
            <h2>Continue exploring</h2>
            <Link to="/resources/maternity/">
              All maternity guides <UiIcon name="arrow" />
            </Link>
          </header>
          <div className="maternity-related-guides">
            <Link className="newborn-related-guide" to="/resources/newborn/how-to-prepare-for-your-newborn-session/">
              <ArticlePhoto
                alt="Newborn baby posed in a soft pink studio setup"
                className="newborn-related-guide__photo"
                label="PHOTO"
                src={newbornRelatedImage}
              />
              <span>How to Prepare for Your Newborn Session</span>
              <UiIcon name="arrow" />
            </Link>
            <Link className="newborn-related-guide" to="/resources/newborn/best-age-for-newborn-photos/">
              <ArticlePhoto
                alt="Awake newborn baby wrapped in soft pink fabric"
                className="newborn-related-guide__photo newborn-related-guide__photo--2"
                label="PHOTO"
                src={newbornAgeImage}
              />
              <span>Best Age for Newborn Photos</span>
              <UiIcon name="arrow" />
            </Link>
          </div>
        </section>
      </div>
    </article>
  </main>
);

export default BestTimeForMaternityPhotos;
